import { useRef } from 'react';

import HorizontalScrollContainer from '../HorizontalScrollContainer';
import * as St from './Carousel.styles';

import { useCarousel } from '~/core/hooks/useCarousel';
import { ImagePreviewModel } from '~/core/models';
import { formatPriceUnitOrFree } from '~/core/utils';
import ArrowButton from '~atoms/ArrowButton';

type CarouselProps = {
  images: ImagePreviewModel[];
};

const Carousel = ({ images }: CarouselProps) => {
  const thumbnailRefs = useRef<(HTMLImageElement | null)[]>([]);

  const { currentIndex, prevIndex, nextIndex, goTo, goToNext, goToPrev } =
    useCarousel(images, 0);

  const scrollThumbnailToIndex = (index: number) => {
    const thumbnail = thumbnailRefs.current[index];

    if (!thumbnail) {
      return;
    }

    thumbnail.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  };

  const currentImage = images[currentIndex];
  const altText = 'thumbnail ' + currentIndex;

  const selectImage = (index: number) => {
    scrollThumbnailToIndex(index);

    goTo(index);
  };

  return (
    <>
      <St.Container>
        <St.ImgContainer>
          <St.CarouselImage src={currentImage.image} alt={altText} />
          <ArrowButton
            direction="left"
            handleClick={goToPrev}
            iconStyleProps={{ fontSize: '40rem' }}
            ariaProps={{ 'aria-label': 'Previous Image' }}
          />
          <ArrowButton
            direction="right"
            handleClick={goToNext}
            iconStyleProps={{ fontSize: '40rem' }}
            ariaProps={{ 'aria-label': 'Next Image' }}
          />
        </St.ImgContainer>
        <St.ProductInfoContainer>
          <St.ProductName>{currentImage.productName}</St.ProductName>
          {currentImage.unitPrice || currentImage.unitPrice === 0 ? (
            <St.ProductCost>
              {formatPriceUnitOrFree(
                currentImage.unitPrice,
                currentImage.unitOfMeasure
              )}
            </St.ProductCost>
          ) : null}
        </St.ProductInfoContainer>
      </St.Container>

      <St.ThumbnailsContainer>
        <HorizontalScrollContainer
          goToNext={goToNext}
          goToPrev={goToPrev}
          currentIndex={currentIndex}
          nextIndex={nextIndex}
          prevIndex={prevIndex}
        >
          {images.map((img, index) => (
            <St.Thumbnail
              key={img.image}
              src={img.image}
              alt={`Thumbnail ${index}`}
              onClick={() => selectImage(index)}
              data-is-selected={index === currentIndex}
              ref={(el) => {
                thumbnailRefs.current[index] = el;
              }}
            />
          ))}
        </HorizontalScrollContainer>
      </St.ThumbnailsContainer>
    </>
  );
};

export default Carousel;
